import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { ChevronRightIcon, HomeIcon } from '../storage/icons';

/**
 * Fil d'Ariane de l'EN-TÊTE — repère de position GLOBAL, persistant sur tous les écrans.
 *
 * HISTORIQUE, et pourquoi on revient en arrière. La v0.12.3 avait DESCENDU le fil de position dans
 * le contenu de l'explorateur et supprimé ce mécanisme partagé, ne laissant en haut qu'une Maison
 * muette. Résultat : deux repères de niveau différent sur le même écran, et un contenu qui
 * s'ouvrait sur sa propre position plutôt que sur son contenu. Le fil remonte donc ENTIER dans
 * l'en-tête, et le contenu n'a plus de fil du tout : le dernier maillon (le dossier courant, en
 * gras) EST le titre de la page — c'est pourquoi il n'est pas répété en dessous.
 *
 * MÉCANISME. L'en-tête est rendu par la coquille (`AppChrome`) tandis que le chemin n'est connu que
 * de la page : il vient du listing de l'API, pas de l'URL — les maillons portent des NOMS de
 * dossiers, là où l'URL ne porte que des uuid. La page POUSSE donc son fil via
 * {@link useSetBreadcrumb} et l'en-tête le LIT.
 *
 * Le fil est RÉINITIALISÉ au démontage de la page qui l'a posé : sans quoi, quitter un dossier pour
 * la console laisserait affiché en haut le chemin du dossier précédent, qui désignerait autre chose
 * que ce qu'on regarde.
 */

/** Un maillon du fil : un libellé, et une cible (`null` = maillon courant, non cliquable). */
export interface Crumb {
  /** Clé de rendu, stable — l'id du dossier en pratique. */
  id: string;
  /** Libellé affiché (nom de dossier, ou nom localisé d'espace pour une racine). */
  label: string;
  /** Cible de navigation, ou `null` pour le maillon COURANT (rendu en titre, non cliquable). */
  href: string | null;
}

interface BreadcrumbContextValue {
  trail: readonly Crumb[];
  setTrail: (trail: readonly Crumb[]) => void;
}

/**
 * Contexte à valeur INERTE par défaut (`setTrail` no-op) plutôt qu'`undefined` : une page montée
 * HORS coquille — c'est le cas de la plupart des tests unitaires, qui rendent un écran seul — doit
 * pouvoir appeler `useSetBreadcrumb` sans lever.
 */
const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  trail: [],
  setTrail: () => {},
});

export function BreadcrumbProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [trail, setTrailState] = useState<readonly Crumb[]>([]);
  const setTrail = useCallback((next: readonly Crumb[]) => setTrailState(next), []);
  const value = useMemo(() => ({ trail, setTrail }), [trail, setTrail]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/**
 * Pousse le fil de la page courante vers l'en-tête, et le RETIRE au démontage.
 *
 * L'appelant reconstruit son tableau à chaque rendu ; on le SÉRIALISE pour la dépendance de
 * l'effet. Comparer les références suffirait à faire boucler indéfiniment (`setTrail` → rendu →
 * nouveau tableau, référence différente à contenu identique → effet → `setTrail` → …).
 */
export function useSetBreadcrumb(trail: readonly Crumb[]): void {
  const { setTrail } = useContext(BreadcrumbContext);
  const key = JSON.stringify(trail);
  useEffect(() => {
    setTrail(JSON.parse(key) as Crumb[]);
    return () => setTrail([]);
  }, [key, setTrail]);
}

/**
 * Rendu du fil dans l'en-tête : Maison (→ `/`) puis les maillons poussés par la page.
 *
 * TYPOGRAPHIE — tout le fil est au corps de texte (16 px), dernier maillon compris. Il n'y a plus
 * de saut de taille : le dossier courant se distingue par le POIDS (`font-medium`) et la COULEUR
 * (`text-fg` contre `text-muted`), jamais par une taille à part.
 *
 * PAS DE SOULIGNEMENT sur les maillons parents. WCAG 1.4.1 (« jamais l'information par la couleur
 * seule ») vise les liens NOYÉS DANS UN BLOC DE TEXTE, où rien d'autre qu'une nuance ne signale
 * qu'ils sont cliquables. Ici les liens vivent dans un `<nav>`/`<ol>` séparés par des chevrons, sans
 * aucun texte non-lien autour : ce sont la structure, la position et les séparateurs qui les
 * identifient. Le survol et le focus visible restent inchangés.
 *
 * LARGEUR — l'en-tête partage sa ligne avec « Déposer » et le menu de compte, qui ne rétrécissent
 * jamais. Un chemin profond doit donc savoir se réduire : chaque maillon tronque à l'ellipse
 * (`truncate` + `min-w-0`), et les maillons INTERMÉDIAIRES s'effacent sous 640 px pour garder
 * lisibles les deux seuls qui comptent alors — la Maison et le dossier courant.
 */
export function HeaderBreadcrumb(): React.JSX.Element {
  const { t } = useI18n();
  const { trail } = useContext(BreadcrumbContext);
  const linkCls =
    'truncate rounded text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

  return (
    <nav aria-label={t('storage.breadcrumb')} className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-base">
        <li className="flex shrink-0 items-center gap-1">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded text-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <HomeIcon className="size-5" />
            <span className="hidden sm:inline">{t('nav.home')}</span>
          </Link>
        </li>
        {trail.map((crumb, i) => {
          const isCurrent = crumb.href === null;
          // Maillons intermédiaires masqués sous 640 px : la Maison et le dossier courant suffisent
          // à se situer, et les garder tous pousserait les actions hors de l'en-tête.
          const responsive = isCurrent || i === trail.length - 1 ? 'flex' : 'hidden sm:flex';
          return (
            <li key={crumb.id} className={`${responsive} min-w-0 items-center gap-1`}>
              <ChevronRightIcon className="size-5 shrink-0 text-muted" />
              {crumb.href === null ? (
                <span className="truncate font-medium text-fg" aria-current="page">
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.href} className={linkCls}>
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
